// Render trigger + preview. POSTs render-request-artifacts; on 200 shows the
// artifact list and fetches the request.yml body (raw yaml) into a <pre> preview.
// On 422 render_validation_failed, parses the joined "code: detail; …" into a
// fatals checklist routing each code to where it's fixed (unknown codes fall
// through to a verbatim row). F1 stops here — creating a run is F3.

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { render as renderApi } from "../../api/endpoints/render";
import { onboardingRequest } from "../../api/endpoints/onboardingRequest";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import type { RenderBundleOut } from "../../api/types/renderBundle";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { CreateRunPanel } from "./CreateRunPanel";

// Where each validate_request() fatal (render/request.py) is fixed.
const RENDER_FATAL_HINTS: Record<string, string> = {
  identity_missing:
    "Site has no resolvable tenant/account — confirm the site exists under a tenant (Accounts → Tenants).",
  allocation_missing: "Allocate the account/site CIDRs + VLANs in the Allocation console.",
  onboarding_request_missing: "Create the onboarding request intent above.",
  site_boundary_slug_missing: "Set the site boundary slug in the request editor above.",
  no_tenant_admin: "Add a tenant admin on the Identity tab.",
  publication_missing: "Configure publication on the Access tab.",
  netbird_missing: "Configure the NetBird overlay on the Access tab.",
  service_profile_missing: "Configure the service profile on the Hosts tab.",
  managed_host_missing: "Add at least one managed host on the Hosts tab.",
};

interface Fatal {
  code: string;
  detail: string;
  hint: string | null;
}

function parseFatals(message: string): Fatal[] {
  return message
    .split("; ")
    .map((part) => {
      const idx = part.indexOf(": ");
      const code = idx === -1 ? part.trim() : part.slice(0, idx).trim();
      const detail = idx === -1 ? "" : part.slice(idx + 2).trim();
      return { code, detail, hint: RENDER_FATAL_HINTS[code] ?? null };
    })
    .filter((f) => f.code !== "");
}

export function RenderPanel({ siteSlug, canWrite }: { siteSlug: string; canWrite: boolean }) {
  const navigate = useNavigate();
  const [snap, setSnap] = useState<
    { bundle: RenderBundleOut; forSite: string; reqUpdatedAt: number } | null
  >(null);

  // Shares the editor's cache; gives us a live dataUpdatedAt to detect request edits.
  const reqQ = useQuery({
    retry: false,
    queryKey: qk.onboardingRequest(siteSlug),
    queryFn: () => onboardingRequest.get(siteSlug).then((r) => r.data),
  });

  const render = useMutation({
    mutationFn: () => renderApi.renderRequest(siteSlug).then((r) => r.data),
    // Drop any prior preview the moment a new render starts, so a subsequent
    // 422 (fatals checklist) can never leave a stale successful bundle on screen.
    onMutate: () => setSnap(null),
    onSuccess: (data) =>
      setSnap({ bundle: data, forSite: siteSlug, reqUpdatedAt: reqQ.dataUpdatedAt }),
  });

  const bundle = snap?.bundle ?? null;
  const stale =
    snap != null &&
    (snap.forSite !== siteSlug || reqQ.dataUpdatedAt !== snap.reqUpdatedAt);

  const requestArtifact = bundle?.artifacts.find((a) => a.name === "request.yml") ?? null;
  const body = useQuery({
    enabled: requestArtifact != null,
    queryKey: qk.renderArtifact(requestArtifact?.content_sha256 ?? "none"),
    queryFn: () => renderApi.artifact(requestArtifact!.content_sha256).then((r) => r.data),
  });

  const validationError =
    render.error instanceof ApiError && render.error.code === "render_validation_failed"
      ? render.error
      : null;
  const otherError = render.error && !validationError ? render.error : null;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {canWrite && (
        <div>
          <button type="button" style={primaryBtn}
            disabled={render.isPending || reqQ.isPending}
            onClick={() => render.mutate()}>
            {render.isPending ? "Rendering…" : "Render request.yml"}
          </button>
        </div>
      )}

      {validationError && (
        <div>
          <h4 style={{ margin: "0 0 8px", fontSize: "0.9rem", color: "var(--crit)" }}>
            Not ready to render — resolve these first:
          </h4>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6 }}>
            {parseFatals(validationError.message).map((f) => (
              <li key={f.code} style={{ fontSize: "0.85rem" }}>
                {f.hint ? (
                  <>
                    <strong>{labelFor(f.code)}:</strong> {f.hint}
                  </>
                ) : (
                  // Unknown code → verbatim fallback so a new backend fatal is never hidden.
                  <code>
                    {f.code}: {f.detail}
                  </code>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {otherError && <ErrorBanner error={otherError} title="Render failed" />}

      {bundle && (
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>
            Rendered ✓ — {bundle.generator_version} · {bundle.artifacts.length} artifact
            {bundle.artifacts.length === 1 ? "" : "s"}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {bundle.artifacts.map((a) => (
              <li key={a.content_sha256} style={{ fontSize: "0.85rem" }}>
                <strong>{a.name}</strong> · {a.byte_size} B ·{" "}
                <code style={{ fontSize: "0.75rem" }}>{a.content_sha256.slice(0, 12)}</code>
              </li>
            ))}
          </ul>
          {bundle.warnings.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, color: "var(--warn, #b58900)" }}>
              {bundle.warnings.map((w, i) => (
                <li key={i} style={{ fontSize: "0.8rem" }}>
                  {w.code}: {w.detail}
                </li>
              ))}
            </ul>
          )}
          {body.isFetching && <p style={{ color: "var(--text-dim)" }}>Loading preview…</p>}
          {body.error && (
            <ErrorBanner error={body.error} title="Failed to load request.yml preview" />
          )}
          {body.data && (
            <pre
              style={{
                margin: 0, padding: 12, background: "var(--surface-2)",
                border: "1px solid var(--border)", borderRadius: 6,
                fontSize: "0.78rem", overflow: "auto", maxHeight: 420,
              }}
            >
              {body.data}
            </pre>
          )}
          <CreateRunPanel
            bundle={bundle}
            stale={stale}
            canWrite={canWrite}
            requestStatus={reqQ.data?.request_status ?? null}
            onCreated={(id) =>
              void navigate({ to: "/onboarding-runs", search: { run: id, tab: "runs" } })
            }
          />
        </div>
      )}
    </div>
  );
}

function labelFor(code: string): string {
  // Human-friendly destination, derived from the hint table's intent.
  if (code === "allocation_missing") return "Allocation console";
  if (code === "publication_missing" || code === "netbird_missing") return "Access tab";
  if (code === "service_profile_missing" || code === "managed_host_missing") return "Hosts tab";
  if (code === "no_tenant_admin") return "Identity tab";
  if (code === "onboarding_request_missing" || code === "site_boundary_slug_missing")
    return "Request editor (above)";
  if (code === "identity_missing") return "Tenant/account setup";
  return code;
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none", padding: "8px 16px",
  borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
